import { addDays } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';

import useReactivateScheduledMessageMutation from 'components/domain/automation/scheduledMessages/useReactivateScheduledMessageMutation';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const useScheduledMessagesReactivateModal = () => {
  const { openConfirmModal } = useAppModal();
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: reactivateScheduledMessage } =
    useReactivateScheduledMessageMutation();

  const openReactivateScheduledMessagesModal = ({
    scheduledMessage,
  }: {
    scheduledMessage: ScheduledMessage;
  }) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="pageAutomations.scheduledMessages.modalReactivate.body"
          components={{ br: <br /> }}
          values={{
            name: scheduledMessage.messageName,
            date: addDays(scheduledMessage.expectedFireTime ?? new Date(), 1),
          }}
        />
      ),
      confirmLabel: t(
        'pageAutomations.scheduledMessages.modalReactivate.title',
      ),
      onConfirmAsync: async () => {
        await reactivateScheduledMessage(
          {
            scheduledMessageUid: scheduledMessage.uid,
          },
          {
            onError,
            onSuccess: () => {
              notifySuccess(
                t('pageAutomations.scheduledMessages.modalReactivate.success'),
              );
            },
          },
        );
      },
      title: t('pageAutomations.scheduledMessages.modalReactivate.title'),
    });
  };

  return { openReactivateScheduledMessagesModal };
};

export default useScheduledMessagesReactivateModal;
